<template>
  <TableList
    v-rect-loading="loading"
    class="alumni-management-list"
    row-key="_id"
    :title="$t(`${i18nPrefix}.title`)"
    :listData="alumniList"
    :tableColumns="tableColumns"
    :actionBtn="actionBtn"
    :showCreateBtn="false"
  >
    <template #btn>
      <el-button class="blue-btn" @click="dialogVisible = true">{{ $t(`${i18nPrefix}.btn.upload`) }}</el-button>
      <el-button class="blue-btn" @click="handelAddAlumni">{{ $t(`${i18nPrefix}.btn.addAlumni`) }}</el-button>
    </template>
    <template #btn-after>
      <el-tooltip class="select-file-tip" effect="dark" placement="top">
        <template #content>
          <div>{{ $t(`${i18nPrefix}.message.refreshTip`) }}</div>
        </template>
        <span>
          <i class="el-icon-question"></i>
        </span>
      </el-tooltip>
    </template>
    <template #disabled="{ data, row }">
      <el-switch :value="!data" @change="onEnabledChange($event, row)"></el-switch>
    </template>
    <template #page-bottom>
      <DialogWrapper
        :loading="uploadLoading"
        :show.sync="dialogVisible"
        >
        <div>
          <label>1. </label>
          <el-button type="text" class="template-link" href="" @click="downloadTemplate">{{ $t(`${i18nPrefix}.btn.downloadTemplate`) }}</el-button>
        </div>
        <div class="upload-input">
          <div class="file-type-tip"><label>2. {{ $t(`${i18nPrefix}.label.excelInput`) }}</label></div>
          <el-input v-model="file.name" readonly="readonly" type="text" id="show_file" class="select-file-input" />
          <el-button class="select-file" size="small" @click="selectFile">{{ $t(`${i18nPrefix}.btn.selectFile`) }}</el-button>
        </div>
        <div>
          <span v-show="showUploadTip" class="file-error-tip">{{ $t(`${i18nPrefix}.message.uploadTip`) }}</span>
          <input type="file" ref="uploadFile" class="upload-file" @change="fileChange" />
        </div>
        <span slot="footer" class="dialog-footer">
          <el-button class="upload-cancel" size="small" @click="handleClose">{{ $t(`${i18nPrefix}.btn.cancelUpload`) }}</el-button>
          <el-button class="upload-submit" size="small" @click="uploadAlumni">{{ $t(`${i18nPrefix}.btn.ConfirmUpload`) }}</el-button>
        </span>
      </DialogWrapper>
    </template>
  </TableList>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import { createI18nProperty } from '@/i18n'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import moment from 'moment'

export default {
  components: {
    TableList,
    DialogWrapper
  },
  data () {
    return {
      i18nPrefix: 'admin.company.alumniManagement',
      loading: false,
      uploadLoading: false,
      fileList: [],
      file: {
        lastModified: null,
        lastModifiedDate: null,
        name: '',
        size: null,
        type: '',
        webkitRelativePath: ''
      },
      fileType: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel', 'application/wps-office.xlsx'],
      alumniList: [],
      dialogVisible: false,
      showUploadTip: false,
      actionBtn: {
        edit: {
          show: true,
          onclick: this.handleEditAlumni
        }
      }
    }
  },
  computed: {
    tableColumns () {
      const columns = [
        {
          label: this.$t(`${this.i18nPrefix}.label.email`),
          dataKey: 'email_address',
          showOverflowTooltip: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.phone`),
          dataKey: 'phone',
          showOverflowTooltip: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.name`),
          dataKey: 'name',
          showOverflowTooltip: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.birthday`),
          dataKey: 'birthday',
          formatter: this.formatDate
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.isEnabled`),
          dataKey: 'is_disabled',
          slotName: 'disabled'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.status`),
          dataKey: 'i18nStatus',
          showOverflowTooltip: true
        }
      ]
      return columns
    }
  },
  created () {
    this.getAlumniEmployeeList()
  },
  methods: {
    ...mapActions('alumniManagement', ['uploadAlumniFile', 'getAlumniList', 'updateAlumni']),

    async getAlumniEmployeeList () {
      this.loading = true
      try {
        const resp = await this.getAlumniList()
        if (resp) {
          this.alumniList = _.map(resp, item => {
            if (item.status === 1) {
              createI18nProperty(item, 'i18nStatus', `${this.i18nPrefix}.label.Active`)
            } else if (item.status === 0) {
              createI18nProperty(item, 'i18nStatus', `${this.i18nPrefix}.label.InActive`)
            } else {
              createI18nProperty(item, 'i18nStatus', `${this.i18nPrefix}.label.Pending`)
            }
            return item
          })
        }
      } finally {
        this.loading = false
      }
    },
    async uploadAlumni () {
      if (!this.handleBeforeUpload(this.file)) {
        return
      }
      this.uploadLoading = true
      try {
        const resp = await this.uploadAlumniFile(this.file)
        if (resp) {
          this.$message({
            message: this.$t(`${this.i18nPrefix}.message.uploadSuccess`, { successCount: resp.success_count, failCount: resp.error_info_list.length }),
            duration: 2000,
            type: 'success'
          })
          this.clearFile()
          this.dialogVisible = false
          this.getAlumniEmployeeList()
        } else {
          this.handleUploadError(resp, this.file)
        }
      } finally {
        this.uploadLoading = false
      }
    },
    handleBeforeUpload (file) {
      if (!this.fileType.includes(file.type)) {
        this.showUploadTip = true
        return false
      }
      return true
    },
    handleUploadError (resp, file) {
      this.$message({
        message: this.$t(`${this.i18nPrefix}.message.uploadError`),
        duration: 2000,
        type: 'warning'
      })
    },
    clearFile () {
      this.$refs.uploadFile.value = ''
      this.file = {
        lastModified: null,
        lastModifiedDate: null,
        name: '',
        size: null,
        type: '',
        webkitRelativePath: ''
      }
    },
    selectFile () {
      this.clearFile()
      this.$refs.uploadFile.dispatchEvent(new MouseEvent('click'))
      this.showUploadTip = false
    },
    fileChange () {
      this.file = this.$refs.uploadFile.files[0]
      if (!this.file) {
        this.file = {
          lastModified: null,
          lastModifiedDate: null,
          name: '',
          size: null,
          type: '',
          webkitRelativePath: ''
        }
      }
    },
    handleClose (done) {
      this.showUploadTip = false
      this.dialogVisible = false
      this.clearFile()
    },
    formatDate (date) {
      return date ? moment(date).format('YYYY-MM-DD') : '-'
    },
    async onEnabledChange (isEnabled, row) {
      this.loading = true
      try {
        const data = {
          id: row.id,
          is_disabled: !isEnabled
        }
        const resp = await this.updateAlumni(data)
        if (resp && resp.id) {
          this.$message.success(this.$t(`${this.i18nPrefix}.message.optionSuccess`))
          row.is_disabled = resp.is_disabled
        } else {
          this.$message.error(this.$t(`${this.i18nPrefix}.message.optionField`))
        }
      } finally {
        this.loading = false
      }
    },
    handelAddAlumni () {
      this.$router.push('/v-admin/alumni/management/add')
    },
    handleEditAlumni (row) {
      this.$router.push(`/v-admin/alumni/management/edit/${row.id}`)
    },
    downloadTemplate () {
      window.location.href = process.env.VUE_APP_BASE_URL + 'static/templates/AlumniTemplate.xlsx'
    }
  }
}
</script>

<style lang="scss" scoped>
.alumni-management-list {
  .upload-input {
    display: flex;
    align-items: center;
    .select-file-input {
      flex: 1;
      margin-left: 10px;
    }
  }
  .upload-cancel {
    background-color: #aaa;
    color:#ffffff;
    width: 86px;
    margin-right: 30px;
    border-width: 0;
    font-weight: 700;
  }
  .upload-submit {
    width: 86px;
    background-color: #0096cc;
    color: #fff;
    border-width: 0;
    font-weight: 700;
  }

  .select-file {
    margin-left: 5px;
    background-color: #aaa;
    color: #ffffff;
    width: 86px;
    border-width: 0;
    font-weight: 700;
  }
  .submit-upload {
    width: 30%;
    margin-left: 15px;
  }
  .upload-file {
    display: none;
  }

  .file-error-tip {
    margin-left: 10px;
    color: red;
  }
  ::v-deep {
    .el-dialog {
      width: 591px;
    }
    .el-dialog__body {
      padding: 30px 40px;
    }
  }
  ::v-deep {
    .table-header {
      height: 100%;
    }
    .select-file-tip {
      vertical-align: middle;
      font-size: 18px;
      margin-left: 24px;
    }
  }
}
</style>
